<template>
  <div class="flex flex-col">
    <div class="toolbar-row h-42px flex justify-between items-center gap-2">
      <div class="inline-flex items-center gap-2 justify-self-start">
        <div class="inline-flex items-center gap-1 p-0 pr-8px cursor-pointer">
          <img class="w-30px h-30px" src="/logo.png" alt="Logo" />
          <n-text type="success" class="text-16px font-600">PuppyCAD</n-text>
        </div>
        <n-menu
          class="editor-main-menu"
          data-onboarding="main-menu"
          mode="horizontal"
          :options="mainMenuOptions"
          :value="null"
          @update:value="handleMainMenuSelect"
        />
      </div>

      <div class="justify-self-center">
        <n-h4 class="!m-0">{{ projectName }}</n-h4>
      </div>

      <div class="inline-flex items-center justify-self-end gap-1">
        <n-button v-if="!readonlyPreview" data-onboarding="submit" type="primary" size="small" @click="emit('submit')">
          <template #icon>
            <n-icon>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                <path fill="currentColor" d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z" />
              </svg>
            </n-icon>
          </template>
          提交
        </n-button>

        <n-divider v-if="!readonlyPreview" vertical class="!h-20px" />

        <Theme />

        <n-button data-onboarding="editor-settings" quaternary size="small" @click="handleOpenEditorSettingsDialog">
          <template #icon>
            <n-icon><Settings /></n-icon>
          </template>
        </n-button>

        <n-divider vertical class="!h-20px" />

        <UserMenu />
      </div>
    </div>

    <n-divider class="!m-0" />

    <n-tabs
      v-if="!readonlyPreview"
      data-onboarding="command-toolbar"
      ref="tabsRef"
      v-model:value="activeTab"
      type="line"
      :animated="false"
      size="small"
      class="toolbar-tabs"
      @update:value="handleTabChange"
    >
      <n-tab-pane
        v-if="isAssemblyMode"
        name="assembly"
        :tab="isEnterEditingComponentStatus ? '特征' : '装配体'"
        style="height: 132px; min-height: 132px; max-height: 132px;"
      >
        <template v-if="isEnterEditingComponentStatus">
          <div class="flex flex-row justify-start w-full h-full">
            <AssemblyToolbarTab
              :assembly-edit-component-disabled="assemblyEditComponentDisabled"
              @command="handlePanelCommand"
              @operation-change="handleOperationChange"
            />

            <FeatureToolbarTab
              class="min-w-0 flex-1"
              @command="handlePanelCommand"
              @operation-change="handleOperationChange"
            />
          </div>
        </template>
        <template v-else>
          <AssemblyToolbarTab
            :assembly-edit-component-disabled="assemblyEditComponentDisabled"
            @command="handlePanelCommand"
            @operation-change="handleOperationChange"
          />
        </template>
      </n-tab-pane>

      <n-tab-pane
        v-if="supportsFeatureCommands"
        name="feature"
        tab="特征"
        style="height: 132px; min-height: 132px; max-height: 132px;"
      >
        <FeatureToolbarTab
          @command="handlePanelCommand"
          @operation-change="handleOperationChange"
        />
      </n-tab-pane>

      <n-tab-pane
        name="sketch"
        tab="草图"
        style="height: 132px; min-height: 132px; max-height: 132px;"
      >
        <SketchToolbarTab
          :current-drawing-tool="currentDrawingTool"
          :is-sketch-plane-selected="isSketchPlaneSelected"
          :shaded-sketch-contours-enabled="shadedSketchContoursEnabled"
          @command="handlePanelCommand"
          @operation-change="handleOperationChange"
        />
      </n-tab-pane>

      <n-tab-pane
        name="mark"
        tab="标注"
        style="height: 132px; min-height: 132px; max-height: 132px;"
      >
        <MarkToolbarTab
          @command="handlePanelCommand"
          @operation-change="handleOperationChange"
        />
      </n-tab-pane>

      <n-tab-pane
        name="evalution"
        tab="评估"
        style="height: 132px; min-height: 132px; max-height: 132px;"
      >
        <EvalutionToolbarTab
          @command="handlePanelCommand"
          @operation-change="handleOperationChange"
        />
      </n-tab-pane>
    </n-tabs>
    <div v-else class="editor-toolbar__readonly-preview">
      <n-text depth="3" class="editor-toolbar__readonly-text">
        {{ READONLY_PREVIEW_MESSAGE }}
      </n-text>
    </div>

    <n-divider class="!m-0" />

    <div v-if="!readonlyPreview" class="toolbar-row toolbar-row-4" data-onboarding="operation-hint">
      <div class="operation-hint-container">
        <div class="operation-hint">
          <n-text v-if="displayedOperation" depth="3" style="font-size: 12px">
            <span style="font-weight: 500">{{ displayedOperation.name }}:</span>
            {{ displayedOperation.hint }}
          </n-text>
          <n-text v-else depth="3" style="font-size: 12px">请选择一个操作...</n-text>
        </div>

        <div v-if="showCancelAction" class="drawing-actions">
          <n-button size="small" @click="emit('cancelDrawing')" quaternary>
            <template #icon>
              <n-icon>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </n-icon>
            </template>
            取消 (Esc)
          </n-button>

          <n-button
            v-if="canUndoPoint"
            size="small"
            @click="emit('undoPoint')"
            quaternary
          >
            <template #icon>
              <n-icon>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z"/>
                </svg>
              </n-icon>
            </template>
            撤销点 (Backspace)
          </n-button>

          <n-button
            v-if="canFinish"
            size="small"
            @click="emit('finishDrawing')"
            type="success"
          >
            <template #icon>
              <n-icon>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"/>
                </svg>
              </n-icon>
            </template>
            完成 (Enter/双击)
          </n-button>
        </div>
      </div>
    </div>

    <n-divider class="!m-0" />

    <!-- ------ 编辑器设置弹窗 开始------ -->
    <SettingsDialog
      v-model:show="isEditorSettingsDialogVisible"
      :initialSettings="persistedSnapSettings"
      @confirm="handleEditorSettingsConfirm"
    />
    <!-- ------ 编辑器设置弹窗 结束------ -->
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import type { MenuOption, TabsInst } from 'naive-ui';
import { storeToRefs } from 'pinia';
import { Settings } from '@vicons/carbon';
import { useAddEvent, useRemoveEvent } from '@puppycad/cad-core';
import FeatureToolbarTab from '@/components/editor/toolbar/FeatureToolbarTab.vue';
import SketchToolbarTab from '@/components/editor/toolbar/SketchToolbarTab.vue';
import MarkToolbarTab from '@/components/editor/toolbar/MarkToolbarTab.vue';
import AssemblyToolbarTab from '@/components/editor/toolbar/AssemblyToolbarTab.vue';
import EvalutionToolbarTab from '@/components/editor/toolbar/EvalutionToolbarTab.vue';
import UserMenu from '@/components/layout/UserMenu.vue';
import SettingsDialog from '@/components/settings/SettingsDialog.vue';
import Theme from '@/components/settings/Theme.vue';
import type { SketchRelationsAndSnapSettings } from '@/config/editor-settings/EditorSettings';
import { useViewer } from '@/hooks';
import { editorPreferencesRepo } from '@/services/db';
import { normalizeEditorPreferencesPatch } from '@/services/db/editor-preferences-snapshot';
import { useToolbarCommandStore } from '@/stores/toolbar-command';
import { useSketchStateStore } from '@/hooks/useSketchState';
import { SketchCornerMode } from '@/core';
// ------ 编辑器装配体工作流 开始------
import { useEditorAssemblyWorkflow } from '@/views/editor/useEditorAssemblyWorkflow';
// ------ 编辑器装配体工作流 结束------

interface EditorToolbarProps {
  /**
   * 当前编辑器项目 ID（来自路由 params）。
   * 用作偏好存储的主键，与 projectStore 解耦。
   */
  projectId: string;

  /**
   * 初始捕捉配置。由父组件从 editorPreferencesRepo 加载后透传。
   * 未传入或 null 时，设置弹窗使用系统默认值。
   */
  initialSnapSettings?: SketchRelationsAndSnapSettings | null;

  projectName?: string;
  currentDrawingTool?: string | null;
  isSketchPlaneSelected?: boolean;
  shadedSketchContoursEnabled?: boolean;
  operationOverride?: ToolbarOperation | null;
  activeTabType?: 'assembly' | 'part' | null;
  supportsFeatureCommands?: boolean;
  /** 历史版本预览态只允许查看，不允许执行建模面板命令。 */
  readonlyPreview?: boolean;
  /** 首屏尚未成功加载或模型正在加载时禁用引导重播。 */
  onboardingDisabled?: boolean;
  /**
   * 为 true 时禁用装配体工具栏「编辑零部件」按钮。
   * 由宿主根据设计树是否选中可编辑装配子件等规则计算后传入。
   */
  assemblyEditComponentDisabled?: boolean;
}

interface ToolbarOperation {
  name: string;
  hint: string;
}

const props = withDefaults(defineProps<EditorToolbarProps>(), {
  initialSnapSettings: null,
  projectName: '未命名项目',
  currentDrawingTool: null,
  isSketchPlaneSelected: false,
  shadedSketchContoursEnabled: false,
  operationOverride: null,
  activeTabType: null,
  supportsFeatureCommands: true,
  readonlyPreview: false,
  onboardingDisabled: true,
  assemblyEditComponentDisabled: true,
});

const emit = defineEmits<{
  command: [commandType: string];
  submit: [];
  exportStep: [];
  exportOfflinePackage: [];
  exportGlb: [];
  importOfflinePackage: [];
  cancelDrawing: [];
  finishDrawing: [];
  undoPoint: [];
  startOnboarding: [];
}>();

/**
 * 编辑器主菜单选项。
 *
 * 设计说明：
 * 1. 顶部入口按工业软件主菜单组织，文件菜单承载返回与导出能力。
 * 2. 尚未接入真实命令的菜单项保持禁用，避免 UI 展示可点击但没有行为的入口。
 */
const mainMenuOptions = computed<MenuOption[]>(() => [
  {
    label: '文件',
    key: 'file',
    children: [
      {
        label: '保存',
        key: 'file-save',
        disabled: true,
      },
      {
        label: '打开文件',
        key: 'file-open',
        disabled: true,
      },
      {
        label: '导入',
        key: 'file-import-offline-package',
      },
      {
        label: '导出',
        key: 'file-export',
        children: [
          {
            label: '导出 STEP',
            key: 'file-export-step',
          },
          {
            label: '导出离线包',
            key: 'file-export-offline-package',
          },
          {
            label: '导出 GLB',
            key: 'file-export-glb',
          },
        ],
      },
    ],
  },
  {
    label: '编辑',
    key: 'edit',
    disabled: props.readonlyPreview,
    children: [
      {
        label: '撤销',
        key: 'edit-undo',
        disabled: props.readonlyPreview,
      },
      {
        label: '重做',
        key: 'edit-redo',
        disabled: props.readonlyPreview,
      },
    ],
  },
  {
    label: '视图',
    key: 'view',
    disabled: true,
  },
  {
    label: '插入',
    key: 'insert',
    disabled: true,
  },
  {
    label: '帮助',
    key: 'help',
    children: [
      {
        label: '新手引导',
        key: 'help-onboarding',
        disabled: props.onboardingDisabled,
      },
    ],
  },
]);

/**
 * 处理主菜单命令。
 *
 * @param key  菜单项 key。
 */
function handleMainMenuSelect(key: string | number): void {
  if (props.readonlyPreview && (key === 'edit-undo' || key === 'edit-redo')) {
    return;
  }
  switch (key) {
    case 'help-onboarding':
      if (!props.onboardingDisabled) emit('startOnboarding');
      break;
    case 'file-export-step':
      emit('exportStep');
      break;
    case 'file-export-offline-package':
      emit('exportOfflinePackage');
      break;
    case 'file-export-glb':
      emit('exportGlb');
      break;
    case 'file-import-offline-package':
      emit('importOfflinePackage');
      break;
    case 'edit-undo':
      dispatchEditorUndoRedoKeyboardEvent('undo');
      break;
    case 'edit-redo':
      dispatchEditorUndoRedoKeyboardEvent('redo');
      break;
  }
}

/**
 * 通过现有全局快捷键入口触发编辑器撤销/重做。
 *
 * 设计说明：撤销/重做已有统一入口处理绘制中撤销点、PropertyManager 预览保护和模型历史队列。
 * 菜单层只分发等价快捷键事件，避免复制历史逻辑或新增旁路入口。
 *
 * @param command 菜单命令类型。
 */
function dispatchEditorUndoRedoKeyboardEvent(command: 'undo' | 'redo'): void {
  window.dispatchEvent(new KeyboardEvent('keydown', {
    key: command === 'undo' ? 'z' : 'y',
    code: command === 'undo' ? 'KeyZ' : 'KeyY',
    ctrlKey: true,
    bubbles: true,
    cancelable: true,
  }));
}

const tabsRef = ref<TabsInst | null>(null);
const activeTab = ref('feature');
const isAssemblyMode = computed(() => props.activeTabType === 'assembly');
const supportsFeatureCommands = computed(() => props.supportsFeatureCommands !== false);
const currentOperation = ref<ToolbarOperation | null>(null);
const READONLY_PREVIEW_MESSAGE = '历史版本仅支持查看';
//------ 编辑器设置弹窗 开始------
const isEditorSettingsDialogVisible = ref<boolean>(false);
const persistedSnapSettings = ref<SketchRelationsAndSnapSettings | null>(
  normalizeEditorPreferencesPatch({
    snapSettings: props.initialSnapSettings ?? undefined,
  }).snapSettings ?? null,
);
const { getViewer } = useViewer();
//------ 编辑器设置弹窗 结束------

const toolbarCommandStore = useToolbarCommandStore();
const { activeCommands } = storeToRefs(toolbarCommandStore);
const sketchStateStore = useSketchStateStore();
const { isSketchActive } = storeToRefs(sketchStateStore);
const currentDrawingTool = computed(() => props.currentDrawingTool);
const isSketchPlaneSelected = computed(() => Boolean(props.isSketchPlaneSelected));
const shadedSketchContoursEnabled = computed(() => Boolean(props.shadedSketchContoursEnabled));

/**
 * 切换工具栏顶部 tab。
 * @param tabName 目标 tab 名称。
 */
async function switchToolbarTab(tabName: string): Promise<void> {
  if (tabName === 'assembly' && !isAssemblyMode.value) {
    return;
  }
  if (tabName === 'feature' && !supportsFeatureCommands.value) {
    return;
  }
  activeTab.value = tabName;
  await nextTick();
  tabsRef.value?.syncBarPosition?.();
}

onMounted(async () => {
  await nextTick();

  useAddEvent('toolbarSwitchTab', switchToolbarTab);
});

onUnmounted(() => {
  useRemoveEvent('toolbarSwitchTab', switchToolbarTab);
});

watch(
  isSketchActive,
  (active) => {
    if (active) {
      void switchToolbarTab('sketch');
    }
  },
);

watch(
  () => props.initialSnapSettings,
  (next) => {
    persistedSnapSettings.value = normalizeEditorPreferencesPatch({
      snapSettings: next ?? undefined,
    }).snapSettings ?? null;
  },
  { deep: true },
);

// 装配体 tab 是 v-if 条件渲染的，当 isAssemblyMode 切换时，tab 会从 DOM 里插入/移除，
// 导致 naive-ui 的下划线指示条测量在 DOM 更新完成前跑完，指示条可能停留在错位的 offsetLeft。
// 这里在 DOM 更新后主动调用 syncBarPosition 重新量一次，让指示条对齐当前 activeTab。
watch(isAssemblyMode, async (isAssembly) => {
  if (isAssembly && activeTab.value === 'feature') {
    activeTab.value = 'assembly';
  }
  if (!isAssembly && activeTab.value === 'assembly') {
    activeTab.value = 'feature';
  }
  if (!supportsFeatureCommands.value && activeTab.value === 'feature') {
    activeTab.value = isAssembly ? 'assembly' : 'sketch';
  }
  await nextTick();
  tabsRef.value?.syncBarPosition?.();
}, { immediate: true });

watch(supportsFeatureCommands, async (enabled) => {
  if (!enabled && activeTab.value === 'feature') {
    activeTab.value = isAssemblyMode.value ? 'assembly' : 'sketch';
  }
  await nextTick();
  tabsRef.value?.syncBarPosition?.();
}, { immediate: true });

const showDrawingActions = computed(() => currentDrawingTool.value !== null && currentDrawingTool.value !== '');
const isSketchCornerActive = computed(() =>
  activeCommands.value.some((command) =>
    command === 'fillet'
    || command === 'chamfer'
    || command === SketchCornerMode.Fillet
    || command === SketchCornerMode.Chamfer,
  ),
);
const showCancelAction = computed(() =>
  !props.readonlyPreview && (showDrawingActions.value || isSketchCornerActive.value),
);
const displayedOperation = computed(() => props.operationOverride ?? currentOperation.value);

const canUndoPoint = computed(() => {
  if (props.readonlyPreview) {
    return false;
  }
  const multiPointTools = ['polyline', 'spline', 'bezier', 'style_spline'];
  return Boolean(currentDrawingTool.value && multiPointTools.includes(currentDrawingTool.value));
});

const canFinish = computed(() => {
  if (props.readonlyPreview) {
    return false;
  }
  const multiPointTools = ['polyline', 'spline', 'bezier', 'style_spline'];
  return Boolean(currentDrawingTool.value && multiPointTools.includes(currentDrawingTool.value));
});

function handleTabChange(): void {
  currentOperation.value = null;
}

/**
 * 绘制工具结束（Esc/完成）后清除操作提示。
 *
 * 设计说明：提示行显示 `operationOverride ?? currentOperation`；绘制工具的提示
 * 只在点击按钮时写入，若结束时不清除，后续命令（如智能尺寸）退出后提示行会
 * 回落到早已结束的绘制提示。
 */
watch(currentDrawingTool, (tool) => {
  if (!tool) {
    currentOperation.value = null;
  }
});

function handlePanelCommand(commandType: string): void {
  /**
   * 面板按钮在只读预览态仍可能被外部脚本触发事件，
   * 因此除提示用户外，命令派发入口也必须做硬拦截。
   */
  if (props.readonlyPreview) {
    return;
  }
  emit('command', commandType);
}

function handleOperationChange(operation: ToolbarOperation): void {
  currentOperation.value = operation;
}

//------ 编辑器设置弹窗 开始------
/**
 * 打开编辑器设置弹窗。
 */
function handleOpenEditorSettingsDialog(): void {
  // 将弹窗显隐状态设置为显示。
  isEditorSettingsDialogVisible.value = true;
}

/**
 * 处理编辑器设置弹窗确认事件。
 * 将设置快照转交给 PointSnapManager 同步各捕捉策略状态，并持久化到编辑器偏好表。
 * @param settings - 来自弹窗的设置快照。
 */
async function handleEditorSettingsConfirm(settings: SketchRelationsAndSnapSettings): Promise<void> {
  // 同步到运行时捕捉管理器。
  const viewer = getViewer();
  viewer?.updatePointSnapSettings?.(settings);
  const normalizedPatch = normalizeEditorPreferencesPatch({ snapSettings: settings });
  persistedSnapSettings.value = normalizedPatch.snapSettings ?? null;

  // 持久化到编辑器偏好表。主键直接用 props.projectId，
  // 不再依赖 projectStore.currentProject（后者在 dev 自动登录直达时可能为 null）。
  await editorPreferencesRepo.upsert(props.projectId, normalizedPatch);
}
//------ 编辑器设置弹窗 结束------


// ------ 编辑装配体工作流 开始------
const assemblyWorkflow = useEditorAssemblyWorkflow();
const isEnterEditingComponentStatus = computed(() => assemblyWorkflow.isEnterEditingComponentStatus);


// ------ 编辑装配体工作流 结束------
</script>

<style scoped lang="less">
.toolbar-row {
  display: flex;
  align-items: center;
  padding: 0 12px;
}

.toolbar-row-4 {
  height: 32px;
}

.icon-button {
  padding: 4px;
  background: transparent;
  border: none;
  font-size: 12px;
  border-radius: 6px;
  cursor: pointer;
}

.icon-button svg {
  width: 16px;
  height: 16px;
}

.editor-main-menu {
  min-width: 360px;
  background: transparent;
  --n-item-height: 34px;
}

.editor-main-menu :deep(.n-menu-item-content) {
  padding: 0 10px;
}

.toolbar-tabs {
  width: 100%;
}

.toolbar-tabs :deep(.n-tabs-nav) {
  margin-bottom: 0;
  padding: 0 12px;
  min-height: 40px;
}

.toolbar-tabs :deep(.n-tab-pane) {
  height: 132px;
  min-height: 132px;
  max-height: 132px;
  padding: 8px 12px;
  box-sizing: border-box;
  overflow: hidden;
}

.toolbar-tabs :deep(.n-tabs-tab) {
  padding: 8px 16px;
  font-size: 13px;
}

.toolbar-tabs :deep(.n-tabs-tab-pad) {
  width: 10px !important;
}

.editor-toolbar__readonly-preview {
  display: flex;
  min-height: 32px;
  align-items: center;
  justify-content: flex-start;
  padding: 0 16px;
  background: var(--n-color);
}

.editor-toolbar__readonly-text {
  font-size: 12px;
  line-height: 1.4;
  text-align: left;
}

.operation-hint-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 16px;
}

.operation-hint {
  padding: 0 8px;
  flex: 1;
}

.drawing-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-right: 8px;
}
</style>
